/**
 * useSocketQueryBridge.ts
 *
 * Central socket → TanStack Query invalidation bridge. Mounted once at the
 * top of `App.tsx` (inside the authenticated tree) so every domain hook
 * stops needing its own ad-hoc `socket.on(EVENT, refresh)` plumbing.
 *
 * When a relevant server-side event fires, the bridge invalidates the
 * matching query-key prefix(es) and any consumer of those queries
 * re-fetches automatically.
 *
 * `EVENT_TO_KEYS` maps each event to the query-key prefixes it invalidates;
 * extend it to add coverage as more domains migrate.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';
import { qk } from '@/lib/queryKeys';
import { SOCKET_EVENTS } from '@kabbi/shared/constants';

/**
 * Each socket event maps to a list of query-key prefixes to invalidate.
 * Multiple prefixes are allowed — e.g. once Rides migrates, a
 * `ride.updated` event will invalidate both `ride` lists and the
 * dispatcher snapshot.
 */
const EVENT_TO_KEYS: Record<string, ReadonlyArray<readonly unknown[]>> = {
  // Driver shift/ride status auto-transitions (tick.ts, every 5 s).
  // Rides invalidated alongside dispatcher because the same tick also
  // transitions ride.status (ASSIGNED → ACCEPTED → ARRIVING → ...).
  [SOCKET_EVENTS.SCHEDULER_UPDATED]:         [qk.dispatcher.all, qk.rides.all],
  // Manual scheduler event add/update/remove. Rides updated together
  // because every scheduler event has a backing ride row.
  [SOCKET_EVENTS.SCHEDULER_SYNC]:            [qk.dispatcher.all, qk.rides.all],
  // Driver break-status flipped from the dispatcher UI itself.
  [SOCKET_EVENTS.DISPATCHER_ACTIVE_CHANGED]: [qk.dispatcher.all],
};

/**
 * Subscribe to every event in `EVENT_TO_KEYS` and invalidate the
 * matching query prefixes when they fire. Re-attaches whenever
 * `enabled` flips — `reconnectSocket()` (called on login) drops every
 * listener via `removeAllListeners()`, so the bridge must run again
 * after auth transitions to bind handlers to the new socket.
 *
 * @param enabled Pass `isAuthenticated`. While false the hook stays
 *   inert; the socket isn't usable without an auth token anyway.
 */
export function useSocketQueryBridge(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const socket = getSocket();
    const handlers: Array<[string, () => void]> = [];

    for (const [event, keys] of Object.entries(EVENT_TO_KEYS)) {
      const handler = () => {
        for (const queryKey of keys) {
          queryClient.invalidateQueries({ queryKey });
        }
      };
      socket.on(event, handler);
      handlers.push([event, handler]);
    }

    return () => {
      for (const [event, handler] of handlers) socket.off(event, handler);
    };
  }, [queryClient, enabled]);
}
